import { useEffect, useMemo, useState } from 'react'
import { Alert, Button, Card, Col, Container, Form, Row, Table } from 'react-bootstrap'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { GitBranch, Route } from 'lucide-react'
import {
  createComplianceWorkflow,
  fetchComplianceWorkflow,
  fetchComplianceWorkflows,
  fetchBusinessEntities,
  fetchDivisions,
  updateComplianceWorkflow,
  type ComplianceWorkflowStepDto,
} from '../services/api'
import { APP_ROUTE_REGISTRY } from '../workflow/routeRegistry'
import { defaultWorkflow } from '../workflow/defaultWorkflow'
import { getVisibleSteps } from '../workflow/getVisibleSteps'
import { isWorkflowOverview, resolveWorkflowStep } from '../workflow/resolveWorkflowStep'
import { mapApiWorkflowToDefinition } from '../workflow/mapApiWorkflow'
import ComplianceWorkflowTrailPreview from '../workflow/ComplianceWorkflowTrailPreview'
import type { WorkflowDefinition, WorkflowStepKey } from '../workflow/types'
import './AdminComplianceWorkflow.css'

const PREVIEW_PATHS = [
  { label: 'Master terms', path: '/master-terms' },
  { label: 'Compliance setup', path: '/pim' },
  { label: 'Products', path: '/products' },
  { label: 'Rule packs', path: '/rules' },
  { label: 'Distributors', path: '/distributors' },
  { label: 'Run checks', path: '/run-checks' },
  { label: 'Findings', path: '/findings' },
  { label: 'Dashboard', path: '/' },
  { label: 'Users (out of flow)', path: '/users' },
]

type EditableStep = {
  id?: string
  stepKey: string
  sortOrder: number
  title: string
  shortLabel: string
  description: string
  primaryRoutePath: string
  routePatterns: string
}

function stepsToEditable(steps: ComplianceWorkflowStepDto[]): EditableStep[] {
  return steps.map((s) => ({
    id: s.id,
    stepKey: s.stepKey,
    sortOrder: s.sortOrder,
    title: s.title,
    shortLabel: s.shortLabel,
    description: s.description,
    primaryRoutePath: s.primaryRoutePath,
    routePatterns: s.routePatterns?.length ? s.routePatterns.join(', ') : s.primaryRoutePath,
  }))
}

export default function AdminComplianceWorkflow() {
  const queryClient = useQueryClient()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [previewPath, setPreviewPath] = useState('/products')
  const [editableSteps, setEditableSteps] = useState<EditableStep[]>([])
  const [workflowName, setWorkflowName] = useState('')
  const [scopeDivisionId, setScopeDivisionId] = useState('')
  const [scopeBusinessEntityId, setScopeBusinessEntityId] = useState('')
  const [saveMessage, setSaveMessage] = useState<string | null>(null)

  const { data: summaries } = useQuery({
    queryKey: ['compliance-workflows'],
    queryFn: fetchComplianceWorkflows,
  })

  const { data: divisions } = useQuery({
    queryKey: ['org', 'divisions'],
    queryFn: fetchDivisions,
  })

  const { data: businessEntities } = useQuery({
    queryKey: ['org', 'business-entities'],
    queryFn: () => fetchBusinessEntities(),
  })

  const activeId = selectedId ?? summaries?.[0]?.id ?? null

  const { data: workflow, isLoading } = useQuery({
    queryKey: ['compliance-workflow', activeId],
    queryFn: () => fetchComplianceWorkflow(activeId!),
    enabled: !!activeId,
  })

  useEffect(() => {
    if (!workflow) return
    setWorkflowName(workflow.name)
    setEditableSteps(stepsToEditable(workflow.steps))
    setScopeDivisionId(workflow.divisionId ?? '')
    setScopeBusinessEntityId(workflow.businessEntityId ?? '')
  }, [workflow])

  const saveMutation = useMutation({
    mutationFn: () =>
      updateComplianceWorkflow(activeId!, {
        name: workflowName,
        divisionId: scopeDivisionId || null,
        businessEntityId: scopeBusinessEntityId || null,
        clearDivision: !scopeDivisionId,
        clearBusinessEntity: !scopeBusinessEntityId,
        steps: editableSteps.map((s, i) => ({
          id: s.id,
          stepKey: s.stepKey,
          sortOrder: i + 1,
          title: s.title,
          shortLabel: s.shortLabel,
          description: s.description,
          primaryRoutePath: s.primaryRoutePath,
          routePatterns: s.routePatterns
            .split(',')
            .map((p) => p.trim())
            .filter(Boolean),
        })),
      }),
    onSuccess: (w) => {
      setSaveMessage('Workflow saved.')
      queryClient.invalidateQueries({ queryKey: ['compliance-workflow'] })
      queryClient.invalidateQueries({ queryKey: ['compliance-workflows'] })
      queryClient.invalidateQueries({ queryKey: ['compliance-workflow', 'active'] })
      queryClient.setQueryData(['compliance-workflow', w.id], w)
    },
    onError: () => setSaveMessage('Save failed — check you are MasterAdmin.'),
  })

  const cloneMutation = useMutation({
    mutationFn: () =>
      createComplianceWorkflow({
        name: `${workflowName} (variant)`,
        cloneFromWorkflowId: activeId!,
        divisionId: scopeDivisionId || undefined,
        businessEntityId: scopeBusinessEntityId || undefined,
      }),
    onSuccess: (w) => {
      setSelectedId(w.id)
      queryClient.invalidateQueries({ queryKey: ['compliance-workflows'] })
      setSaveMessage(`Cloned workflow: ${w.code}`)
    },
  })

  const previewDefinition = useMemo((): WorkflowDefinition => {
    if (editableSteps.length > 0) {
      return {
        code: workflow?.code ?? defaultWorkflow.code,
        name: workflowName,
        version: workflow?.version ?? 1,
        steps: editableSteps.map((s, i) => ({
          key: s.stepKey as WorkflowStepKey,
          sortOrder: i + 1,
          title: s.title,
          shortLabel: s.shortLabel,
          description: s.description,
          primaryRoute: s.primaryRoutePath,
          routePatterns: s.routePatterns
            .split(',')
            .map((p) => p.trim())
            .filter(Boolean),
        })),
      }
    }
    if (workflow) return mapApiWorkflowToDefinition(workflow)
    return defaultWorkflow
  }, [editableSteps, workflow, workflowName])

  const previewResolved = resolveWorkflowStep(previewDefinition, previewPath)
  const previewOverview = isWorkflowOverview(previewPath)
  const previewWindow = getVisibleSteps(
    previewDefinition.steps,
    previewResolved?.index ?? null,
    2,
    2,
  )

  const mappedPatterns = new Set<string>()
  editableSteps.forEach((s) => {
    s.routePatterns
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean)
      .forEach((p) => mappedPatterns.add(p))
  })

  const unmappedRoutes = APP_ROUTE_REGISTRY.filter(
    (r) => r.stepKey != null && !mappedPatterns.has(r.pattern),
  )

  return (
    <Container className="py-4 admin-compliance-workflow">
      <div className="d-flex align-items-start gap-3 mb-4">
        <div className="rounded-circle bg-primary bg-opacity-10 p-3 text-primary">
          <GitBranch size={28} aria-hidden />
        </div>
        <div>
          <h1 className="h3 mb-1">Compliance workflow</h1>
          <p className="text-muted mb-0 small">
            Review and edit the MIM compliance journey. Changes apply to the trail shown on in-flow pages.
            Clone a workflow to assign a variant to a division or business entity.
          </p>
        </div>
      </div>

      {saveMessage && (
        <Alert variant="info" dismissible onClose={() => setSaveMessage(null)}>
          {saveMessage}
        </Alert>
      )}

      <Row className="g-3 mb-4">
        <Col lg={4}>
          <Card className="shadow-sm h-100">
            <Card.Body>
              <Card.Title className="h6">Workflows</Card.Title>
              <div className="list-group list-group-flush">
                {summaries?.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className={`list-group-item list-group-item-action ${s.id === activeId ? 'active' : ''}`}
                    onClick={() => setSelectedId(s.id)}
                  >
                    <div className="fw-semibold">{s.name}</div>
                    <div className="small opacity-75">
                      {s.code}
                      {s.isDefault && ' · default'}
                      {s.divisionId && ' · division'}
                      {s.businessEntityId && ' · business entity'}
                    </div>
                  </button>
                ))}
              </div>
              <Button
                variant="outline-primary"
                size="sm"
                className="mt-3"
                disabled={!activeId || cloneMutation.isPending}
                onClick={() => cloneMutation.mutate()}
              >
                Clone for org variant
              </Button>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={8}>
          <Card className="shadow-sm mb-3">
            <Card.Body>
              <Card.Title className="h6 d-flex align-items-center gap-2">
                <Route size={18} aria-hidden />
                Trail preview
              </Card.Title>
              <Form.Group className="mb-3">
                <Form.Label className="small">Pretend current page</Form.Label>
                <Form.Select size="sm" value={previewPath} onChange={(e) => setPreviewPath(e.target.value)}>
                  {PREVIEW_PATHS.map((p) => (
                    <option key={p.path} value={p.path}>
                      {p.label} ({p.path})
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <div className="border rounded overflow-hidden mb-2">
                <ComplianceWorkflowTrailPreview visible={previewWindow} isOverview={previewOverview} />
              </div>
              <p className="small text-muted mb-0">
                Window for <code>{previewPath}</code>:{' '}
                {previewWindow.steps.map((s) => s.shortLabel).join(' · ') || '—'}
                {previewResolved
                  ? ` (current: ${previewResolved.step.shortLabel})`
                  : previewOverview
                    ? ' (overview)'
                    : ' (out of flow)'}
              </p>
            </Card.Body>
          </Card>

          <Card className="shadow-sm">
            <Card.Body>
              <Card.Title className="h6">Edit workflow</Card.Title>
              {isLoading && <p className="small text-muted">Loading…</p>}
              {!isLoading && workflow && (
                <>
                  <Row className="g-2 mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="small">Name</Form.Label>
                        <Form.Control
                          size="sm"
                          value={workflowName}
                          onChange={(e) => setWorkflowName(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="small">Division scope</Form.Label>
                        <Form.Select
                          size="sm"
                          value={scopeDivisionId}
                          onChange={(e) => setScopeDivisionId(e.target.value)}
                        >
                          <option value="">Global</option>
                          {divisions?.map((d) => (
                            <option key={d.id} value={d.id}>
                              {d.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="small">Business entity</Form.Label>
                        <Form.Select
                          size="sm"
                          value={scopeBusinessEntityId}
                          onChange={(e) => setScopeBusinessEntityId(e.target.value)}
                        >
                          <option value="">None</option>
                          {businessEntities?.map((be) => (
                            <option key={be.id} value={be.id}>
                              {be.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Table responsive size="sm" className="align-middle mb-3">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Key</th>
                        <th>Short label</th>
                        <th>Title</th>
                        <th>Primary route</th>
                        <th>Route patterns</th>
                      </tr>
                    </thead>
                    <tbody>
                      {editableSteps.map((step, idx) => (
                        <tr key={step.id ?? `${step.stepKey}-${idx}`}>
                          <td>{idx + 1}</td>
                          <td>
                            <Form.Control
                              size="sm"
                              value={step.stepKey}
                              onChange={(e) => {
                                const next = [...editableSteps]
                                next[idx] = { ...next[idx], stepKey: e.target.value }
                                setEditableSteps(next)
                              }}
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              value={step.shortLabel}
                              onChange={(e) => {
                                const next = [...editableSteps]
                                next[idx] = { ...next[idx], shortLabel: e.target.value }
                                setEditableSteps(next)
                              }}
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              value={step.title}
                              onChange={(e) => {
                                const next = [...editableSteps]
                                next[idx] = { ...next[idx], title: e.target.value }
                                setEditableSteps(next)
                              }}
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              value={step.primaryRoutePath}
                              onChange={(e) => {
                                const next = [...editableSteps]
                                next[idx] = { ...next[idx], primaryRoutePath: e.target.value }
                                setEditableSteps(next)
                              }}
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              value={step.routePatterns}
                              onChange={(e) => {
                                const next = [...editableSteps]
                                next[idx] = { ...next[idx], routePatterns: e.target.value }
                                setEditableSteps(next)
                              }}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  {unmappedRoutes.length > 0 && (
                    <Alert variant="warning" className="small py-2">
                      <strong>Unmapped in-flow routes</strong> (add to route patterns):{' '}
                      {unmappedRoutes.map((r) => r.pattern).join(', ')}
                    </Alert>
                  )}

                  <Button
                    variant="primary"
                    size="sm"
                    disabled={saveMutation.isPending}
                    onClick={() => saveMutation.mutate()}
                  >
                    Save workflow
                  </Button>
                  <span className="ms-2 small text-muted">Version {workflow.version}</span>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  )
}
